/**
 * WHERE an interaction card's answer goes — the one owner of that decision.
 *
 * `InteractionCard` produces a `RespondBody` and hands it to its host. The only route is
 * session-keyed (lane v2): `POST /api/sessions/{s}/asks/{a}/respond`. MSA refuses `input`,
 * `answers`, `edits`, `override_reason` and `selected` there (its ask store has no field for them),
 * so they are STRIPPED; and the interaction's `subject` is ALWAYS echoed back unchanged — the server
 * answers only against that digest, which is what makes an approval about the content the human
 * actually saw.
 */
import type { RespondBody } from "./interactions";
import { respondToAsk, type AskRespondBody } from "./laneV2/api";
import type { AskResolution } from "./laneV2/types";
import type { Interaction } from "./protocol";

export type RespondTarget = { kind: "session"; sessionId: string };

export type RespondOutcome = { kind: "session"; out: AskResolution };

/**
 * The body a session-keyed ask accepts, from what the card built. Pure.
 *
 * Only `option_id`, `free_text` and `ref_id` survive; `subject` is the interaction's, never the
 * body's (a card cannot answer against a digest it was not shown).
 */
export function sessionAskBody(interaction: Pick<Interaction, "subject">, body: RespondBody): AskRespondBody {
  const out: AskRespondBody = {};
  if (typeof body.option_id === "string" && body.option_id) out.option_id = body.option_id;
  if (typeof body.free_text === "string" && body.free_text.trim()) out.free_text = body.free_text;
  if (typeof body.ref_id === "string" && body.ref_id) out.ref_id = body.ref_id;
  if (interaction.subject) out.subject = interaction.subject;
  return out;
}

/** Send one answer to the session-keyed ask route. */
export async function respondVia(
  target: RespondTarget,
  interaction: Pick<Interaction, "interaction_id" | "subject">,
  body: RespondBody,
): Promise<RespondOutcome> {
  return {
    kind: "session",
    out: await respondToAsk(target.sessionId, interaction.interaction_id, sessionAskBody(interaction, body)),
  };
}
